/**
 * Supabase reports a failed email-link or OAuth round-trip by redirecting back
 * to the app with `error`, `error_code` and `error_description` in the URL
 * hash (implicit flow) or query string (OAuth provider errors).
 *
 * The route guards then `<Navigate replace>` to /auth, which drops the hash —
 * so the user landed on a blank sign-in form with no idea their confirmation
 * or reset link had expired. We capture the error once at module load (before
 * any redirect runs) and let the auth pages surface it.
 */
export interface AuthUrlError {
  code: string | null;
  message: string;
}

function readAuthUrlError(): AuthUrlError | null {
  if (typeof window === 'undefined') return null;
  const { hash, search } = window.location;
  for (const raw of [hash.replace(/^#/, ''), search.replace(/^\?/, '')]) {
    if (!raw.includes('error')) continue;
    const params = new URLSearchParams(raw);
    const description = params.get('error_description');
    const error = params.get('error');
    if (!description && !error) continue;
    const code = params.get('error_code');
    return { code, message: friendlyMessage(code, description ?? error ?? '') };
  }
  return null;
}

function friendlyMessage(code: string | null, fallback: string): string {
  if (code === 'otp_expired') {
    return 'This email link is invalid or has expired. Please request a new one.';
  }
  if (code === 'access_denied' || fallback === 'access_denied') {
    return 'Sign-in was cancelled or denied. Please try again.';
  }
  return fallback;
}

let pendingError: AuthUrlError | null = readAuthUrlError();

/** The auth error Supabase passed back in the URL on this page load, if any. */
export function getAuthUrlError(): AuthUrlError | null {
  return pendingError;
}

/** Forget the captured error once a page has shown it. */
export function clearAuthUrlError(): void {
  pendingError = null;
}
